import {
  ArrowRight,
  CheckCircle2,
  CircleDot,
  Gauge,
  Hourglass,
  Play,
} from "lucide-react";
import {
  ActionButton,
  CustomerPanel,
  EmptyState,
  MetricCard,
} from "./CustomerUI";

import { scanIdentity } from "../../lib/scanIdentity";

function recommendation(value) {
  if (value.corrections_pending > 0) {
    return {
      title: "Review actions awaiting approval",
      description: `${value.corrections_pending} correction action${value.corrections_pending === 1 ? " is" : "s are"} waiting for review.`,
      action: "Review latest scan",
      kind: "review",
    };
  }
  if (value.issue_occurrences > 0) {
    return {
      title: "Choose corrections from your latest findings",
      description: `CleanIQ found ${value.issue_occurrences} issue occurrence${value.issue_occurrences === 1 ? "" : "s"} in this scan. Review the report to choose what to correct.`,
      action: "Review latest scan",
      kind: "review",
    };
  }
  return {
    title: "Keep the clean baseline current",
    description: "No unresolved issues remain in the latest completed scans. Run another scan when your CRM data changes.",
    action: "Run another scan",
    kind: "scan",
  };
}

function healthImprovementDisplay(healthImprovement) {
  if (healthImprovement == null) return "—";
  const points = Number(healthImprovement);
  if (!Number.isFinite(points)) return "—";
  return `${points > 0 ? "+" : ""}${points} ${Math.abs(points) === 1 ? "point" : "points"}`;
}

export default function DashboardValueSummary({
  latestScan,
  issuesCorrected = 0,
  hasConnection,
  onRunScan,
  onOpenScan,
  onConnect,
  onViewInsights,
}) {
  if (!latestScan) {
    return (
      <CustomerPanel
        className="mb-ui-6"
        title="Your CleanIQ results"
        description="Your verified CRM improvement will appear here after the first completed scan."
        data-testid="dashboard-value-empty"
      >
        <EmptyState
          icon={Play}
          title="Run your first scan"
          description={
            hasConnection
              ? "Start with a read-only scan to establish your CRM health and issue baseline."
              : "Connect a CRM first, then run a read-only scan to establish your baseline."
          }
          action={(
            <ActionButton onClick={hasConnection ? onRunScan : onConnect}>
              <Play />
              {hasConnection ? "Run your first scan" : "Connect a CRM"}
            </ActionButton>
          )}
        />
      </CustomerPanel>
    );
  }

  const nextStep = recommendation(latestScan);
  const identity = scanIdentity(latestScan);
  const actionForMetric = (metricValue, destination) => {
    const numericValue = Number(metricValue);
    return Number.isFinite(numericValue) && numericValue !== 0
      ? destination
      : undefined;
  };
  return (
    <CustomerPanel
      className="mb-ui-6 break-words"
      title={`Latest scan · ${identity.name}`}
      description={`${identity.provider || "CRM"} · ${identity.dateLabel}. Findings from this scan; applied corrections below cover all CRMs.`}
      data-testid="dashboard-value-summary"
    >
      <div className="border-b border-slate-200 bg-slate-50 px-ui-4 py-ui-4 sm:flex sm:items-center sm:justify-between sm:gap-ui-4 sm:px-ui-5" data-testid="dashboard-next-action">
        <div>
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-brand">Recommended next step</div>
          <h3 className="mt-ui-1 text-base font-bold text-slate-950">{nextStep.title}</h3>
          <p className="mt-ui-1 text-sm leading-6 text-slate-600">{nextStep.description}</p>
        </div>
        <div className="mt-ui-4 flex flex-col gap-ui-2 sm:mt-0 sm:shrink-0">
          <ActionButton onClick={nextStep.kind === "review" ? onOpenScan : onRunScan}
            disabled={nextStep.kind === "review" ? !onOpenScan : !onRunScan}>
            {nextStep.kind === "scan" ? <Play /> : <ArrowRight />}{nextStep.action}
          </ActionButton>
          <ActionButton variant="secondary" onClick={onViewInsights}>View CRM Insights</ActionButton>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-ui-3 p-ui-4 pb-0 min-[390px]:grid-cols-2 sm:p-ui-5 sm:pb-0">
        <MetricCard
          data-testid="dashboard-issues-found"
          label="Issues CleanIQ found"
          value={latestScan.issue_occurrences}
          detail="Issue occurrences CleanIQ surfaced in this scan."
          icon={CircleDot}
          onClick={actionForMetric(latestScan.issue_occurrences, onOpenScan)}
        />
        <MetricCard
          data-testid="dashboard-actions-awaiting"
          label="Corrections awaiting approval"
          value={latestScan.corrections_pending}
          detail={
            latestScan.corrections_pending > 0
              ? `Review these corrections in the ${latestScan.connector_name || "CRM"} scan report.`
              : latestScan.issue_occurrences > 0
                ? "No corrections are queued. Review this scan's findings to choose your next action."
                : "No correction actions are waiting for your approval."
          }
          icon={Hourglass}
          onClick={actionForMetric(latestScan.corrections_pending, onOpenScan)}
        />
      </div>
      <div className="grid grid-cols-1 gap-ui-3 p-ui-4 min-[390px]:grid-cols-2 lg:grid-cols-3 sm:p-ui-5">
        <MetricCard
          data-testid="dashboard-health-score"
          label="CRM health"
          value={latestScan.health_score == null ? "—" : `${latestScan.health_score}/100`}
          detail="Measures this CRM's data condition, not CleanIQ performance."
          icon={Gauge}
          onClick={actionForMetric(latestScan.health_score, onOpenScan)}
        />
        <MetricCard
          data-testid="dashboard-health-improvement"
          label="Health improvement"
          value={healthImprovementDisplay(latestScan.health_improvement)}
          detail={latestScan.health_improvement == null
            ? "Run this CRM again to measure change from its first completed scan."
            : "Change in this CRM's health points from its first completed scan."}
          icon={ArrowRight}
          onClick={actionForMetric(latestScan.health_improvement, onOpenScan)}
        />
        <MetricCard
          data-testid="dashboard-issues-corrected"
          label="Issues corrected"
          value={issuesCorrected}
          detail="Corrections applied across all connected CRMs."
          icon={CheckCircle2}
          onClick={actionForMetric(issuesCorrected, onViewInsights)}
        />
      </div>
    </CustomerPanel>
  );
}

export { healthImprovementDisplay, recommendation };
